import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { contactInfo } from '../mockData';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { createStaggerContainer, fadeInLeft, fadeInRight, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.3 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.18, delayChildren: 0.1 });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <motion.section id="contact" className="py-12 sm:py-16 lg:py-20 bg-[#F5F0E8]" {...scrollAnimationProps} variants={containerVariants}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-8 sm:mb-12" variants={createStaggerContainer({ staggerChildren: 0.12 })}>
          <motion.p className="text-xs sm:text-sm uppercase tracking-widest text-[#7D8471] mb-3 sm:mb-4 font-medium" variants={fadeInUp}>
            Contact
          </motion.p>
          <motion.h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#5A5045] mb-3 sm:mb-4" variants={fadeInUp}>
            Contactez-moi
          </motion.h2>
          <motion.p className="text-sm sm:text-base lg:text-lg text-[#6B5E54] max-w-2xl mx-auto px-4" variants={fadeInUp}>
            Des questions sur mes services, besoin d'informations ou pour prendre rendez-vous ? N'hésitez pas à me contacter
          </motion.p>
        </motion.div>

        <motion.div className="grid lg:grid-cols-2 gap-8 lg:gap-12" variants={containerVariants}>
          <motion.div variants={createStaggerContainer({ staggerChildren: 0.12 })}>
            <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5A5045] mb-6 sm:mb-8" variants={fadeInLeft}>
              Informations de contact
            </motion.h3>

            <motion.div className="space-y-4 sm:space-y-6" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
              <motion.div variants={scaleIn}>
                <Card className="border-none shadow-md bg-white rounded-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="text-[#7D8471]" size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5A5045] mb-2">Adresse</h4>
                        <p className="text-sm text-[#6B5E54] mb-1">{contactInfo.address.street}</p>
                        <p className="text-sm text-[#6B5E54] mb-3">{contactInfo.address.city}</p>
                        <motion.a
                          href={contactInfo.address.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center space-x-2 text-[#7D8471] hover:text-[#6B7361] transition-colors duration-300 text-sm font-medium"
                          whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                        >
                          <span>Voir sur Google Maps</span>
                          <ExternalLink size={14} />
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="border-none shadow-md bg-white rounded-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="text-[#7D8471]" size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5A5045] mb-2">Téléphone</h4>
                        <p className="text-lg sm:text-xl font-semibold text-[#5A5045] mb-2">{contactInfo.phone.display}</p>
                        <p className="text-sm text-[#6B5E54] mb-3">{contactInfo.phone.hours}</p>
                        <motion.a href={`tel:${contactInfo.phone.link}`} whileHover={shouldReduceMotion ? undefined : { y: -2 }}>
                          <Button size="sm" className="bg-[#7D8471] hover:bg-[#6B7361] text-white text-xs sm:text-sm">
                            Appeler maintenant
                          </Button>
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="border-none shadow-md bg-white rounded-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="text-[#7D8471]" size={20} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-base sm:text-lg font-serif text-[#5A5045] mb-2">Email</h4>
                        <p className="text-sm text-[#6B5E54] mb-2 break-all">{contactInfo.email.address}</p>
                        <motion.a href={`mailto:${contactInfo.email.address}`} whileHover={shouldReduceMotion ? undefined : { y: -2 }}>
                          <Button size="sm" className="bg-[#7D8471] hover:bg-[#6B7361] text-white text-xs sm:text-sm">
                            Envoyer un email
                          </Button>
                        </motion.a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div variants={scaleIn}>
                <Card className="border-none shadow-md bg-white rounded-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#7D8471]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="text-[#7D8471]" size={20} />
                      </div>
                      <div>
                        <h4 className="text-base sm:text-lg font-serif text-[#5A5045] mb-2">Horaires d'ouverture</h4>
                        <p className="text-sm text-[#6B5E54]">{contactInfo.schedule.weekdays}</p>
                        <p className="text-sm text-[#6B5E54]">{contactInfo.schedule.weekend}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div variants={fadeInRight}>
            <motion.h3 className="text-2xl sm:text-3xl font-serif text-[#5A5045] mb-6 sm:mb-8" variants={fadeInUp}>
              Envoyez-moi un message ou prenez rendez-vous
            </motion.h3>

            <motion.form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
              <motion.div variants={fadeInUp}>
                <label htmlFor="name" className="block text-sm font-medium text-[#5A5045] mb-2">
                  Nom et Prénom
                </label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white border-[#7D8471]/30 focus:border-[#7D8471]"
                  required
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <label htmlFor="email" className="block text-sm font-medium text-[#5A5045] mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-white border-[#7D8471]/30 focus:border-[#7D8471]"
                  required
                />
              </motion.div>

              <motion.div variants={fadeInUp}>
                <label htmlFor="message" className="block text-sm font-medium text-[#5A5045] mb-2">
                  Message
                </label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-white border-[#7D8471]/30 focus:border-[#7D8471] min-h-[150px]"
                  required
                />
              </motion.div>

              <motion.div variants={scaleIn}>
                <Button
                  type="submit"
                  className="w-full bg-[#7D8471] hover:bg-[#6B7361] text-white py-5 sm:py-6 text-sm sm:text-base rounded-sm transition-all duration-300 hover:shadow-xl"
                >
                  Envoyer
                </Button>
              </motion.div>
            </motion.form>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ContactSection;